import { Observable } from '@apollo/client';
import * as Sentry from '@sentry/react';

const SENSITIVE_HEADERS = new Set(['authorization', 'cookie', 'set-cookie', 'x-api-key']);
const MAX_FIELD_BYTES = 2048;

export const redactHeaders = (headers = {}) =>
    Object.fromEntries(
        Object.entries(headers).map(([name, value]) => {
            const key = name.toLowerCase();
            if (SENSITIVE_HEADERS.has(key) || key.includes('token')) {
                return [name, '[redacted]'];
            }
            return [name, value];
        }),
    );

export const truncate = (value, max = MAX_FIELD_BYTES) => {
    if (value === undefined || value === null) {
        return value;
    }
    let serialized;
    try {
        serialized = JSON.stringify(value);
    } catch {
        return '[unserializable]';
    }
    if (serialized === undefined) {
        return undefined;
    }
    if (serialized.length <= max) {
        return value;
    }
    return `${serialized.slice(0, max)}…[truncated ${serialized.length - max} chars]`;
};

const getOperationType = operation =>
    operation.query?.definitions?.find(d => d.kind === 'OperationDefinition')?.operation;

const buildBreadcrumb = (operation, { errors, networkError } = {}) => {
    const { headers } = operation.getContext();
    const level = errors || networkError ? 'error' : 'info';
    const type = errors || networkError ? 'error' : 'http';
    return {
        category: 'graphql',
        type,
        level,
        message: operation.operationName || 'anonymous',
        data: {
            operationType: getOperationType(operation),
            variables: truncate(operation.variables),
            headers: redactHeaders(headers),
            ...(errors && { errors: truncate(errors) }),
            ...(networkError && { networkError: networkError.message || String(networkError) }),
        },
    };
};

export default (operation, forward) =>
    new Observable(observer => {
        const subscription = forward(operation).subscribe({
            next: result => {
                Sentry.addBreadcrumb(buildBreadcrumb(operation, { errors: result?.errors }));
                observer.next(result);
            },
            error: networkError => {
                Sentry.addBreadcrumb(buildBreadcrumb(operation, { networkError }));
                observer.error(networkError);
            },
            complete: observer.complete.bind(observer),
        });
        return () => subscription.unsubscribe();
    });
